import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";

export function FieldLabel({ children, hint, required }) {
  return (
    <div className="mb-2">
      <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] flex items-center gap-2">
        <span>{children}</span>
        {required && <span className="text-[#F59E0B]">*</span>}
      </div>
      {hint && <div className="text-xs text-[#7a7a80] mt-1 font-light">{hint}</div>}
    </div>
  );
}

export function WhyBox({ children }) {
  return (
    <div className="mb-6 p-4 rounded-md border border-white/5 bg-white/[0.02] text-xs text-[#A1A1AA] font-light leading-relaxed">
      <span className="text-[#F59E0B] uppercase tracking-[0.2em] text-[10px] mr-2">Why we ask</span>
      {children}
    </div>
  );
}

export function TextField({ label, hint, value, onChange, placeholder, maxLength, required, type = "text", testId }) {
  return (
    <div>
      <FieldLabel hint={hint} required={required}>{label}</FieldLabel>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testId}
        className="hm-input w-full px-4 py-3 rounded-md text-sm font-light placeholder:text-[#5b5b60]"
      />
      {maxLength && (
        <div className="text-[10px] text-[#7a7a80] mt-1 text-right font-light">
          {(value ?? "").length} / {maxLength}
        </div>
      )}
    </div>
  );
}

export function TextareaField({ label, hint, value, onChange, placeholder, maxLength, rows = 4, testId }) {
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        data-testid={testId}
        className="hm-input w-full px-4 py-3 rounded-md text-sm font-light placeholder:text-[#5b5b60] resize-none"
      />
      {maxLength && (
        <div className="text-[10px] text-[#7a7a80] mt-1 text-right font-light">
          {(value ?? "").length} / {maxLength}
        </div>
      )}
    </div>
  );
}

export function NumberField({ label, hint, value, onChange, min, max, placeholder, testId }) {
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="hm-input w-full px-4 py-3 rounded-md text-sm font-light placeholder:text-[#5b5b60]"
      />
    </div>
  );
}

export function SelectField({ label, hint, value, onChange, options, testId }) {
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="hm-input w-full px-4 py-3 rounded-md text-sm font-light appearance-none"
        style={{ backgroundImage: "linear-gradient(45deg, transparent 50%, #A1A1AA 50%), linear-gradient(135deg, #A1A1AA 50%, transparent 50%)", backgroundPosition: "calc(100% - 20px) 50%, calc(100% - 14px) 50%", backgroundSize: "6px 6px", backgroundRepeat: "no-repeat" }}
      >
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
            {typeof o === "string" ? o : o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function SearchableSelectField({ label, hint, value, onChange, options, placeholder = "Search...", testId, allowCustom = true }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const normalized = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalized) return options;
    return options
      .filter((option) => option.toLowerCase().includes(normalized));
  }, [normalized, options]);

  const commit = (nextValue) => {
    onChange(nextValue);
    setQuery(nextValue);
    setOpen(false);
  };

  return (
    <div className="relative">
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="relative">
        <input
          value={open ? query : value || ""}
          onFocus={() => {
            setQuery(value || "");
            setOpen(true);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (allowCustom) onChange(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(filtered[0] || query.trim());
            }
            if (e.key === "Escape") setOpen(false);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder={placeholder}
          data-testid={testId}
          className="hm-input w-full px-4 py-3 pr-10 rounded-md text-sm font-light placeholder:text-[#5b5b60]"
        />
        <ChevronsUpDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" strokeWidth={1.5} />
      </div>
      {open && (
        <div className="absolute z-50 mt-2 w-full max-h-64 overflow-auto rounded-md border border-white/10 bg-[#101010] shadow-2xl">
          {filtered.map((option) => (
            <button
              key={option}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit(option)}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 ${option === value ? "text-[#F59E0B]" : "text-[#F5F5F0]"}`}
            >
              {option}
            </button>
          ))}
          {allowCustom && query.trim() && !options.includes(query.trim()) && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit(query.trim())}
              className="w-full text-left px-4 py-2.5 text-sm text-[#F59E0B] hover:bg-white/5"
            >
              Use "{query.trim()}"
            </button>
          )}
          {filtered.length === 0 && !query.trim() && (
            <div className="px-4 py-3 text-sm text-[#A1A1AA]">Start typing to search.</div>
          )}
        </div>
      )}
    </div>
  );
}

export function SearchableMultiSelectField({ label, hint, value = [], onChange, options, placeholder = "Search...", testId, allowCustom = true }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => new Set(value), [value]);
  const normalized = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    return options
      .filter((option) => !selected.has(option))
      .filter((option) => !normalized || option.toLowerCase().includes(normalized));
  }, [normalized, options, selected]);

  const add = (item) => {
    const next = item.trim();
    if (!next || selected.has(next)) return;
    onChange([...value, next]);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative">
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2 mb-3">
        {value.map((item) => (
          <span key={item} className="px-3 py-1.5 rounded-full text-xs font-light border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] inline-flex items-center gap-1">
            {item}
            <button type="button" onClick={() => onChange(value.filter((v) => v !== item))}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(filtered[0] || query);
            }
            if (e.key === "Escape") setOpen(false);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder={placeholder}
          data-testid={testId}
          className="hm-input w-full px-4 py-3 pr-10 rounded-md text-sm font-light placeholder:text-[#5b5b60]"
        />
        <ChevronsUpDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" strokeWidth={1.5} />
        {open && (
          <div className="absolute z-50 mt-2 w-full max-h-64 overflow-auto rounded-md border border-white/10 bg-[#101010] shadow-2xl">
            {filtered.map((option) => (
              <button key={option} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(option)} className="w-full text-left px-4 py-2.5 text-sm text-[#F5F5F0] hover:bg-white/5">
                {option}
              </button>
            ))}
            {allowCustom && query.trim() && !options.includes(query.trim()) && (
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(query)} className="w-full text-left px-4 py-2.5 text-sm text-[#F59E0B] hover:bg-white/5">
                Add "{query.trim()}"
              </button>
            )}
            {filtered.length === 0 && !(allowCustom && query.trim()) && (
              <div className="px-4 py-3 text-sm text-[#A1A1AA]">No options found.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function TagPicker({
  label,
  hint,
  options,
  value = [],
  onChange,
  allowCustom = true,
  customPlaceholder = "Add your own…",
  testId,
  customValue,
  setCustomValue,
  onCommitCustom,
}) {
  const toggle = (tag) => {
    if (value.includes(tag)) onChange(value.filter((t) => t !== tag));
    else onChange([...value, tag]);
  };
  const commitCustom = () => {
    const next = customValue?.trim();
    if (!next) return;
    if (onCommitCustom) {
      onCommitCustom(next);
      return;
    }
    if (!value.some((tag) => tag.toLowerCase() === next.toLowerCase())) {
      onChange([...value, next]);
    }
    setCustomValue?.("");
  };
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2" data-testid={testId}>
        {options.map((tag) => {
          const active = value.includes(tag);
          return (
            <button
              type="button"
              key={tag}
              onClick={() => toggle(tag)}
              className={`px-3 py-1.5 rounded-full text-xs font-light border transition-colors ${
                active
                  ? "bg-[#F59E0B] text-black border-[#F59E0B]"
                  : "border-white/10 text-[#A1A1AA] hover:border-[#F59E0B]/50 hover:text-[#F5F5F0]"
              }`}
            >
              {active && <Check className="w-3 h-3 inline-block -mt-0.5 mr-1" strokeWidth={2.5} />}
              {tag}
            </button>
          );
        })}
      </div>
      {allowCustom && (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={customValue ?? ""}
            onChange={(e) => setCustomValue?.(e.target.value)}
            placeholder={customPlaceholder}
            className="hm-input flex-1 px-4 py-2 rounded-md text-sm font-light placeholder:text-[#5b5b60]"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitCustom();
              }
            }}
          />
          <button
            type="button"
            onClick={commitCustom}
            className="hm-btn-ghost px-3 rounded-md text-xs"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
      {value.filter((v) => !options.includes(v)).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value
            .filter((v) => !options.includes(v))
            .map((tag) => (
              <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-light border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] inline-flex items-center gap-1">
                {tag}
                <button type="button" onClick={() => onChange(value.filter((t) => t !== tag))}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
        </div>
      )}
    </div>
  );
}

export function RadioCards({ label, hint, options, value, onChange, columns = 3, testId }) {
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} data-testid={testId}>
        {options.map((opt) => {
          const key = typeof opt === "string" ? opt : opt.key;
          const optLabel = typeof opt === "string" ? opt : opt.label;
          const hintText = typeof opt === "string" ? null : opt.hint;
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={`text-left p-4 rounded-md border transition-all ${
                active
                  ? "border-[#F59E0B] bg-[#F59E0B]/5"
                  : "border-white/10 bg-[#0f0f0f] hover:border-white/20"
              }`}
            >
              <div className={`text-sm font-medium ${active ? "text-[#F5F5F0]" : "text-[#F5F5F0]"}`}>{optLabel}</div>
              {hintText && <div className="text-xs text-[#A1A1AA] mt-1 font-light">{hintText}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CheckList({ label, hint, options, value = [], onChange, columns = 2, testId }) {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} data-testid={testId}>
        {options.map((opt) => {
          const active = value.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md border text-sm text-left transition-colors ${
                active
                  ? "border-[#F59E0B]/60 bg-[#F59E0B]/5 text-[#F5F5F0]"
                  : "border-white/10 bg-[#0f0f0f] text-[#A1A1AA] hover:border-white/20"
              }`}
            >
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-sm border ${active ? "bg-[#F59E0B] border-[#F59E0B]" : "border-white/20"}`}>
                {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
              </span>
              <span className="font-light">{opt}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SliderField({ label, hint, min = 0, max = 10, step = 1, value, onChange, leftLabel, rightLabel, testId, format }) {
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="flex items-center gap-4">
        {leftLabel && <span className="text-xs text-[#A1A1AA] font-light w-24 text-right">{leftLabel}</span>}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testId}
          className="flex-1 accent-[#F59E0B]"
        />
        {rightLabel && <span className="text-xs text-[#A1A1AA] font-light w-24">{rightLabel}</span>}
        <span className="w-14 text-right text-sm font-serif-editorial text-[#F59E0B]">{format ? format(value) : value}</span>
      </div>
    </div>
  );
}

export function SwitchField({ label, hint, value, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <div className="text-sm text-[#F5F5F0]">{label}</div>
        {hint && <div className="text-xs text-[#A1A1AA] mt-1 font-light">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        data-testid={testId}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${value ? "bg-[#F59E0B]" : "bg-white/10"}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

export function DayPicker({ label, hint, days, value = [], onChange, testId }) {
  const toggle = (d) => {
    if (value.includes(d)) onChange(value.filter((x) => x !== d));
    else onChange([...value, d]);
  };
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="flex gap-2 flex-wrap" data-testid={testId}>
        {days.map((d) => {
          const active = value.includes(d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => toggle(d)}
              className={`w-14 py-2 rounded-md border text-xs uppercase tracking-wider transition-colors ${
                active
                  ? "border-[#F59E0B] bg-[#F59E0B] text-black font-semibold"
                  : "border-white/10 text-[#A1A1AA] hover:border-white/20"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RangeSlider({ label, hint, min, max, value = [min, max], onChange, testId }) {
  const [lo, hi] = value;
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="flex items-center gap-4">
        <input
          type="number"
          value={lo}
          min={min}
          max={hi}
          onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
          className="hm-input w-20 px-3 py-2 rounded-md text-sm text-center"
        />
        <div className="relative flex-1 h-6">
          <div className="absolute top-1/2 -translate-y-1/2 w-full h-1 bg-white/10 rounded-full" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-1 bg-[#F59E0B] rounded-full"
            style={{ left: `${((lo - min) / (max - min)) * 100}%`, right: `${100 - ((hi - min) / (max - min)) * 100}%` }}
          />
          <input
            type="range" min={min} max={max} value={lo}
            onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
            data-testid={testId ? `${testId}-min` : undefined}
            className="absolute inset-0 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#F59E0B] [&::-webkit-slider-thumb]:cursor-pointer"
          />
          <input
            type="range" min={min} max={max} value={hi}
            onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
            data-testid={testId ? `${testId}-max` : undefined}
            className="absolute inset-0 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#F59E0B] [&::-webkit-slider-thumb]:cursor-pointer"
          />
        </div>
        <input
          type="number"
          value={hi}
          min={lo}
          max={max}
          onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
          className="hm-input w-20 px-3 py-2 rounded-md text-sm text-center"
        />
      </div>
    </div>
  );
}
